import React from "react"
import {
  Alert as MuiAlert,
  Box,
  TextField,
  InputLabel,
  Button,
  Snackbar,
  Tooltip,
} from "@mui/material"
import { HiOutlinePaperClip } from "react-icons/hi"

const getContactValues = contact =>
  Object.entries(contact).reduce(
    (contact, [name, { value }]) => ({ ...contact, [name]: value }),
    {}
  )
const formValid = contact => {
  for (const { isInvalid } of Object.values(contact)) {
    if (isInvalid) return false
  }
  return true
}
const isFieldInvalid = (name, value) => {
  if (name === "hp_contact") return false

  const mailRegex = new RegExp(
    /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/,
    "g"
  )
  if (name !== "PhoneNumber" && value === "") return true

  if (name === "eMail") return !mailRegex.test(value)

  if (name === "PhoneNumber") {
    if (value.length === 0) return false

    const phone = value.replaceAll(/[+,\s]+/gi, "")
    return isNaN(phone) || phone.length < 9
  }

  return false
}
const getSeverity = requestStatus => {
  if (requestStatus === "pending") return "primary"
  if (requestStatus !== "idle") return requestStatus
  return "primary"
}

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
})

const Upload = ({ handleFile, file, label = "Add CV", dirty }) => {
  const inputRef = React.useRef(null)
  return (
    <Box
      width={{ xs: 1, sm: 300 }}
      mt={{ xs: 5, sm: 3, md: 0 }}
      sx={{
        ".file-input": { display: "none" },
      }}
    >
      <input
        ref={inputRef}
        onChange={handleFile}
        name="CV"
        className="file-input"
        type="file"
        accept="application/pdf"
      />
      <Tooltip title="Accepted formats: .pdf">
        <Button
          onClick={() => inputRef?.current?.click()}
          variant="text"
          startIcon={<HiOutlinePaperClip color="#9d9d9d" />}
          sx={({ palette }) => ({
            color: "#9d9d9d",
            border: "1px solid",
            borderColor:
              file.isInvalid && dirty ? palette.error.main : palette.divider,
            borderRadius: 1,
            px: { xs: "32px !important", sm: "72px !important" },
            py: "12px !important",
            width: 1,

            ":hover": {
              color: "#0b0b0b !important",
              svg: {
                color: `#0b0b0b !important`,
                transition: "color 0.25s",
              },
            },
          })}
        >
          {file.value ? file.value.name : label + "*"}
        </Button>
      </Tooltip>
    </Box>
  )
}

const Label = ({ children }) => (
  <InputLabel
    sx={{
      fontSize: { xs: "0.75rem !important", md: "1rem !important" },
      fontWeight: 600,
      color: "text.secondary",
      mb: { xs: 0.5, md: 1 },
    }}
  >
    {children}
  </InputLabel>
)

const getDefaultContact = () => ({
  FirstName: { isInvalid: true, value: "" },
  LastName: { isInvalid: true, value: "" },
  eMail: { isInvalid: true, value: "" },
  PhoneNumber: { isInvalid: false, value: "" },
  Message: { isInvalid: true, value: "" },
  hp_contact: { isInvalid: false, value: "" }, //honeypot
})

const ContactForm = ({
  hasCV = false,
  postURI = "careers",
  fileFieldName = "CV",
  fileInputLabel = "Add CV",
  sx = {},
}) => {
  const [contact, setContact] = React.useState(getDefaultContact())
  const [file, setFile] = React.useState({ isInvalid: hasCV, value: null })
  const [dirty, setDirty] = React.useState(false)
  const [requestStatus, setRequestStatus] = React.useState("idle")
  const [snackOpen, setSnackOpen] = React.useState(false)

  const onChange = ({ target: { name, value } }) =>
    setContact(contact => ({
      ...contact,
      [name]: { isInvalid: isFieldInvalid(name, value), value },
    }))
  const handleFile = e =>
    setFile(() => ({ isInvalid: !e.target.files[0], value: e.target.files[0] }))

  const sendMessage = async () => {
    if (!dirty) setDirty(true)
    if (
      !formValid(contact) ||
      file.isInvalid ||
      requestStatus === "pending" ||
      snackOpen
    )
      return
    if (contact.hp_contact.value !== "") {
      setSnackOpen(true)
      setRequestStatus(() => "success")
      setContact(() => getDefaultContact())
      setDirty(false)

      return
    }

    const copiedContact = { ...contact }
    delete copiedContact.hp_contact

    try {
      setRequestStatus(() => "pending")
      const formData = new FormData()
      const data = { ...getContactValues(copiedContact) }

      formData.append("data", JSON.stringify(data))
      if (file.value) {
        const fileData = file.value
        formData.append(`files.${fileFieldName}`, fileData, fileData.name)
      }

      const response = await fetch(
        `https://strapi-cms.infostudio.ba/api/${postURI}`,
        {
          method: "POST",
          mode: "cors",
          body: formData,
          headers: {
            Authorization: `Bearer 303a54b85e7374c3cfbe343b9f3ff1f77cd0b971e1f998904815df9ef940115a97bdaa481e1e1bc4b41312bd59121ee49dfa83eda82a8caec7320ff30c187cfc19f3615e011fc386e025f19cd74ebd294600816268d5c2536f6ba7cea5e230835f6979bbc097ee87882f820ec08c3909696d98c3e1f0a46f4250771b928179fb`,
          },
        }
      )

      if (response.status === 200) {
        setRequestStatus(() => "success")
        setContact(() => getDefaultContact())
        setFile(() => ({ isInvalid: hasCV, value: null }))
        setDirty(false)
      } else setRequestStatus(() => "error")
      setSnackOpen(true)
    } catch (error) {
      setRequestStatus(() => "error")
      setSnackOpen(true)
    }
  }

  return (
    <>
      <Snackbar
        open={snackOpen}
        autoHideDuration={6000}
        onClose={() => {
          setSnackOpen(false)
          setTimeout(() => setRequestStatus("idle"), 100)
        }}
      >
        <Alert
          onClose={() => {
            setSnackOpen(false)
            setTimeout(() => setRequestStatus("idle"), 100)
          }}
          severity={getSeverity(requestStatus)}
          sx={{ width: "100%" }}
        >
          {requestStatus === "pending" && "Sending mail, please wait"}
          {requestStatus === "success" && "Your email has been sent"}
          {requestStatus === "error" && "An error has occurred"}
        </Alert>
      </Snackbar>

      <Box
        bgcolor="white"
        flex={1}
        px={{ xs: 4, md: 5 }}
        py={7.5}
        borderRadius={4}
        boxShadow="5px 5px 20px 10px rgba(35, 66, 101, 0.05)"
        width={1}
        maxWidth={800}
        sx={sx}
      >
        <form>
          <Box display="flex" flexDirection="column" gap={2.5}>
            <Box
              display="flex"
              flexDirection={{
                xs: "column",
                md: "row",
              }}
              gap={2.5}
            >
              <Box display="flex" flexDirection="column" flex={1}>
                <Label>First Name*</Label>
                <TextField
                  value={contact.FirstName.value}
                  variant="outlined"
                  color="primary"
                  name="FirstName"
                  onChange={onChange}
                  error={dirty && contact.FirstName.isInvalid}
                  sx={({ palette }) => ({
                    borderRadius: 0,
                    fieldset: {
                      border: `1px solid ${palette.divider}`,
                    },
                  })}
                />
              </Box>
              <Box display="flex" flexDirection="column" flex={1}>
                <Label>Last Name*</Label>
                <TextField
                  value={contact.LastName.value}
                  variant="outlined"
                  name="LastName"
                  onChange={onChange}
                  error={dirty && contact.LastName.isInvalid}
                  sx={({ palette }) => ({
                    borderRadius: 0,
                    fieldset: {
                      border: `1px solid ${palette.divider}`,
                    },
                  })}
                />
              </Box>
            </Box>

            <Box
              display="flex"
              gap={2.5}
              flexDirection={{
                xs: "column",
                md: "row",
              }}
            >
              <Box display="flex" flexDirection="column" flex={1}>
                <Label>E-mail*</Label>
                <TextField
                  value={contact.eMail.value}
                  variant="outlined"
                  type="email"
                  name="eMail"
                  onChange={onChange}
                  error={dirty && contact.eMail.isInvalid}
                  sx={({ palette }) => ({
                    borderRadius: 0,
                    fieldset: {
                      border: `1px solid ${palette.divider}`,
                    },
                  })}
                />
              </Box>
              <Box display="flex" flexDirection="column" flex={1}>
                <Label>Phone Number</Label>
                <TextField
                  value={contact.PhoneNumber.value}
                  variant="outlined"
                  name="PhoneNumber"
                  onChange={onChange}
                  error={dirty && contact.PhoneNumber.isInvalid}
                  sx={({ palette }) => ({
                    borderRadius: 0,
                    fieldset: {
                      border: `1px solid ${palette.divider}`,
                    },
                  })}
                />
              </Box>
            </Box>
            <Box display="flex" flexDirection="column" flex={1}>
              <Label>Message*</Label>
              <TextField
                value={contact.Message.value}
                variant="outlined"
                fullWidth
                sx={({ palette }) => ({
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      // height: 184,
                      border: `1px solid ${palette.divider}`,
                    },
                  },
                })}
                rows={5}
                multiline
                name="Message"
                onChange={onChange}
                error={dirty && contact.Message.isInvalid}
              />
            </Box>
            {hasCV && (
              <Upload
                label={fileInputLabel}
                file={file}
                dirty={dirty}
                handleFile={handleFile}
              />
            )}
            <Box
              display="flex"
              justifyContent={{ xs: "center", sm: "flex-start" }}
              mt={1}
            >
              <Button
                variant="contained"
                size="medium"
                sx={{ maxWidth: 190 }}
                onClick={sendMessage}
              >
                Send message
              </Button>

              {/* A BAIT FOR BOTS FIELD */}
              <TextField
                name="hp_contact"
                onChange={onChange}
                sx={{ visibility: "hidden", width: 0, height: 0 }}
              />
            </Box>
          </Box>
        </form>
      </Box>
    </>
  )
}

export default ContactForm
